import _ from "lodash";
import React, { useState, useLayoutEffect, useEffect } from "react";
import { useDispatch } from "Hooks/useDispatch";

// UI Components
import I18nHOC from "UIComponents/I18nHOC";
import SelectEditTemplate from "./SelectEditTemplate";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import Loading from "UIComponents/Loading";

// GraphQL
import { getStaffListQuery } from "./modules/AssessmentToolChooseListV2Queries";
import { getOrganizationRubricsFromCache } from "Rubric/modules/RubricGraphqlHelpers";
import { getOrganizationChecklistsFromCache } from "Checklist/modules/ChecklistGraphqlHelpers";
import { getOrganizationSinglePointRubricsFromCache } from "SinglePointRubric/modules/SinglePointRubricGraphqlHelpers";
import { getOrganizationRubricsQuery } from "Rubric/modules/RubricQuery";
import { getOrganizationChecklistQuery } from "Checklist/modules/ChecklistQuery";
import { getOrganizationSinglePointRubricsQuery } from "SinglePointRubric/modules/SinglePointRubricQuery";
import {
  getOrganizationDpAssessmentRubricQuery,
  getOrganizationStandardRubricsQuery,
  getOrganizationRubricsAndScoreBasedRubricsQuery,
} from "modules/AssessmentTool/AssessmentToolQuery";
import {
  getOrganizationDpAssessmentRubricFromCache,
  writeAssessmentToolOfParentInCache,
} from "modules/AssessmentTool/AssessmentToolGraphqlHelpers";
import { getSubjectAndGenericTagByCourseQuery } from "ClassRoom/modules/ClassRoomQuery";
import { getSubjectAndGenericTagByCourseFromCache } from "ClassRoom/modules/ClassRoomGraphqlHelpers";
import { getLearningCourseAssessmentToolsQuery } from "LearningCourse/routes/AssessmentToolsSetup/modules/AssessmentToolsSetupQueries";
import {
  convertRubricToScoreBasedRubric,
  getUpdatedAssessmentToolDetails,
} from "modules/AssessmentTool/AssessmentToolModule";

// External lib.
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import moment from "moment";

// Utils and Services
import { getAssessmentToolFilters } from "Administrator/modules/Utils";
import { getLabelValueForDropdown, GENERIC_TAGS_INFO } from "modules/Services";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import {
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  TODDLE_LIBRARY,
  RUBRIC,
  ASSESSMENT,
  DP_ASSESSMENT_RUBRIC,
  GRADE_SCALE_TOOL,
} from "Constants/stringConstants";
import {
  getAssessmentSubjects,
  getFieldValue,
  getLevelsForSubjectOrCourseBasedSetup,
} from "../../Assessments/modules/Utils";
import {
  getIsSubjectBasedGradingEnabled,
  getIsLearningCoursesEnabled,
  getIsCourseBasedGradingEnabled,
} from "Teacher/routes/LearningCourses/modules/Utils";
import { getUserId, getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getAssessmentToolItem } from "Utils/AssessmentToolHelpers";

// Design System
import {
  SearchInput,
  ChecklistDropdown,
  FilterLabelButton,
  Table,
  EmptyState,
  Tag,
} from "@toddle-edu/ds-web";
import { ChevronRightOutlined } from "@toddle-edu/ds-icons";
import { ProjectOverviewIllustration } from "@toddle-edu/ds-theme";

// Scss
import classes from "./AssessmentToolChooseListV2.scss";

const styles = {
  arrowStyle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
};

const getEmptyRows = (count = 6) => {
  return _.map(new Array(count), (_, index) => {
    const rowData = [
      {
        key: "name",
        value: <SkeletonLoader width="100%" height="20px" />,
      },
      {
        key: "genericTags",
        value: (
          <div style={{ display: "flex", gap: "8px" }}>
            <SkeletonLoader width="60px" height="20px" variant="rectangular" />
            <SkeletonLoader width="80px" height="20px" variant="rectangular" />
          </div>
        ),
      },
      {
        key: "subType",
        value: <SkeletonLoader width="100%" height="20px" />,
      },
      {
        key: "createdBy",
        value: <SkeletonLoader width="100%" height="20px" />,
      },
      {
        key: "lastEdited",
        value: <SkeletonLoader width="100%" height="20px" />,
      },
      {
        key: "arrowIcon",
        value: <SkeletonLoader width="24px" height="24px" variant="circular" />,
      },
    ];

    return {
      id: `skeleton-${index}`,
      rowData,
      disableRowClick: true,
    };
  });
};

const getColumnList = ({ t }) => [
  {
    key: "name",
    entity: "TITLE",
    value: t("common:name"),
    sortType: "toggle",
    style: { width: "380px" },
  },
  {
    key: "genericTags",
    value: t("common:level"),
    style: {
      width: "290px",
    },
  },
  {
    key: "subType",
    value: t("common:type"),
  },
  {
    key: "createdBy",
    value: t("common:created_by"),
    entity: "CREATED_BY",
    sortType: "dropdown",
    sortOptions: [
      {
        key: "FIRSTNAME_AZ",
        label: t("common:first_name_az"),
        sortEntity: "FIRST_NAME",
        direction: "ASC",
      },
      {
        key: "FIRSTNAME_ZA",
        label: t("common:first_name_za"),
        sortEntity: "FIRST_NAME",
        direction: "DESC",
      },
      {
        key: "LASTNAME_AZ",
        label: t("common:last_name_az"),
        sortEntity: "LAST_NAME",
        direction: "ASC",
      },
      {
        key: "LASTNAME_ZA",
        label: t("common:last_name_za"),
        sortEntity: "LAST_NAME",
        direction: "DESC",
      },
    ],
    style: { width: "440px" },
  },
  {
    key: "lastEdited",
    entity: "UPDATED_AT",
    value: t("common:last_edited_label"),
    sortType: "toggle",
    style: { width: "380px" },
  },
  {
    key: "arrowIcon",
    style: styles.arrowStyle,
  },
];

const steps = [
  {
    value: "TEMPLATE_LIST",
  },
  {
    value: "SELECTED_TEMPLATE",
  },
  {
    value: "EDIT_TEMPLATE",
  },
];

const getCellData = ({ t, column, list, onTemplateClick }) => {
  const { key } = column;
  switch (key) {
    case "name": {
      let { title = "" } = list;
      const label = _.get(list, "label", "");
      title = _.isEmpty(title) ? label : title;
      return !_.isEmpty(title) ? title : t("common:untitled");
    }
    case "subType": {
      const { subType = "" } = list;
      const isExternal = subType === "EXTERNAL";

      const label = isExternal ? t("common:external") : t("common:internal");
      const subTypeText = t("common:assessment_with_type", {
        label,
      });
      return !_.isEmpty(subType) ? subTypeText : t("common:untitled");
    }
    case "genericTags": {
      const genericTags = _.get(list, "genericTags", []);
      return !_.isEmpty(genericTags) ? (
        <div className={classes.tagsContainer}>
          {_.map(genericTags, tag => {
            return (
              <Tag
                key={tag?.id}
                size="small"
                color={_.get(GENERIC_TAGS_INFO, `${tag?.label}.color`, "teal")}
              >
                {tag?.label}
              </Tag>
            );
          })}
        </div>
      ) : null;
    }
    case "createdBy": {
      return getFormattedUserName({ user: list?.createdBy });
    }
    case "lastEdited": {
      const updatedAt = _.get(list, "updatedAt") ?? _.get(list, "lastEdited");
      return moment(updatedAt).format("DD MMM YYYY");
    }
    case "arrowIcon": {
      return (
        <ChevronRightOutlined
          variant={"subtle"}
          tabIndex={0}
          className={classes.chevronRightOutlinedIcon}
          onKeyDown={e =>
            (e.key == " " || e.key === "Enter") && onTemplateClick(list.id)
          }
        />
      );
    }
  }
};

const getBodyData = ({ t, columnList, toolList, onTemplateClick }) => {
  return _.map(toolList, list => {
    const listId = _.get(list, "id");

    const rowData = _.map(columnList, column => {
      return {
        key: _.get(column, "key"),
        value: getCellData({ t, column, list, onTemplateClick }),
      };
    });

    return {
      id: listId,
      rowData,
    };
  });
};

const FeedListComp = props => {
  const {
    t,
    type,
    assessmentId,
    toolList,
    updateToolId,
    updateCurrentStep,
    isDPRubric,
    isTOK,
    filterConfig,
    updateFilterConfig,
    testId,
    showLearningCourseTools,
    academicYearId,
    onChangeConvertedScoreBasedRubricId,
    convertedScoreBasedRubricId,
    removeTool,
    isLoading,
    isData,
    networkStatus,
  } = props;

  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  const dispatch = useDispatch();

  let columnList = getColumnList({ t });

  if (isDPRubric) {
    const requiredHeaders = ["name", "subType", "arrowIcon"];
    if (!isTOK) {
      requiredHeaders?.push("genericTags");
    }
    columnList = _.filter(columnList, ({ key }) =>
      _.includes(requiredHeaders, key)
    );
  } else {
    columnList = _.filter(
      columnList,
      ({ key }) => !_.includes(["genericTags", "subType"], key)
    );
  }

  const onTemplateClick = async id => {
    setShowFullScreenLoader(true);
    try {
      // if user clicks on a rubric, we convert it to score based rubric,
      // now lets say user navigated back and clicked on same rubric again or a different rubric
      // then we delete the previously converted score based rubric (a new one will be created)
      if (convertedScoreBasedRubricId) {
        await removeTool({
          toolId: convertedScoreBasedRubricId,
          toolType: SCORE_BASED_RUBRIC,
          shouldToggleModal: false,
        });
      }

      // if user clicks on a rubric, we convert it to score based rubric,
      const isRubricClicked = _.some(toolList, {
        id,
        assessmentToolType: RUBRIC,
      });

      if (type === SCORE_BASED_RUBRIC && isRubricClicked) {
        // 1. Call mutation to convert rubric to score based rubric
        const input = {
          toolId: id,
          parentId: assessmentId,
          parentType: ASSESSMENT,
        };

        const assessmentToolItem = await dispatch(
          convertRubricToScoreBasedRubric({ input })
        );

        // 2. Write the converted score based rubric to cache
        id = _.get(assessmentToolItem, "id");

        if (assessmentToolItem && id) {
          onChangeConvertedScoreBasedRubricId(id);

          const assessmentToolDetails = dispatch(
            getUpdatedAssessmentToolDetails({
              parentId: input.parentId,
              parentType: input.parentType,
              mutationResponse: assessmentToolItem,
              assessmentToolType: SCORE_BASED_RUBRIC,
            })
          );

          writeAssessmentToolOfParentInCache({
            id: input.parentId,
            type: input.parentType,
            data: assessmentToolDetails,
            academicYearId,
          });
        }

        if (!id) return;
      }

      updateToolId(id);
      updateCurrentStep("SELECT_TEMPLATE");
    } finally {
      setShowFullScreenLoader(false);
    }
  };

  const showInitialLoader = networkStatus === 1;
  const showSkeletonLoader = networkStatus === 2 && isLoading && !isData;

  const data = getBodyData({ t, columnList, toolList, onTemplateClick });

  return (
    <>
      {showInitialLoader ? (
        <div className={classes.loadingContainer}>
          <Loading />
        </div>
      ) : !_.isEmpty(data) || showSkeletonLoader ? (
        <div className={classes.feedListContainer}>
          <Table
            headers={columnList}
            data={showSkeletonLoader ? getEmptyRows() : data}
            onRowClick={onTemplateClick}
            filterConfig={filterConfig}
            filterCallback={updateFilterConfig}
            testId={testId}
            dsVersion="2.0"
          />
        </div>
      ) : (
        <EmptyState
          illustration={ProjectOverviewIllustration}
          title={t("common:no_with_label", {
            label: t("common:template_plural"),
          })}
          subtitle={
            showLearningCourseTools
              ? t("classRoom:no_templates_added_to_the_course_yet")
              : t("classRoom:no_templates_subtext")
          }
        />
      )}

      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </>
  );
};

const TemplateList = props => {
  const {
    searchText,
    updateFooterDetails,
    handleTemplateSearch,
    staffList,
    createdBy,
    updateTemplateCreatedBy,
    t,
    isDPRubric,
    testId,
    userId,
    assessmentToolTemplateUserFilterPreference = {},
    type,
    curriculumProgramId,
  } = props;

  useLayoutEffect(() => {
    updateFooterDetails({ showFooter: false });
  }, []);

  useLayoutEffect(() => {
    if (!assessmentToolTemplateUserFilterPreference || !type || !userId) {
      return;
    }
    const userPreferenceObj = _.get(
      assessmentToolTemplateUserFilterPreference,
      curriculumProgramId,
      {}
    );
    const toolFilterPreferenceObj = _.get(userPreferenceObj, type, {});
    // If createdBy is not set in user preference, default to current user
    const createdByFilter = _.get(toolFilterPreferenceObj, "createdBy", [
      userId,
    ]);

    const filteredCreatedByFilter = _.filter(createdByFilter, createdById => {
      return _.some(staffList, staff => staff.value === createdById);
    });

    updateTemplateCreatedBy(filteredCreatedByFilter);
  }, []);

  const staffLabelValue = getLabelValueForDropdown({
    selectedItems: createdBy,
    itemList: staffList,
    t,
    noneSelectedText: t("common:select"),
  });

  return (
    <>
      {!isDPRubric && (
        <div className={classes.actions}>
          <div className={classes.searchInput}>
            <SearchInput
              value={searchText}
              onChange={_.debounce(handleTemplateSearch, 200)}
              placeholder={t("common:search")}
              testId={`${testId}-templateSearch`}
              focusSearchBySlash
            />
          </div>
          <div
            data-test-id={`${testId}-creatorFilter-button`}
            className={classes.filerButtons}
          >
            <ChecklistDropdown
              options={staffList}
              showAllOption={true}
              value={createdBy}
              onChange={updateTemplateCreatedBy}
              testId={testId}
              isSearchEnabled
              overlayStyle={{ zIndex: 100 }}
            >
              <FilterLabelButton
                dsVersion="2.0"
                variant="neutral"
                type="plain"
                label={t("common:creator")}
                labelValue={staffLabelValue}
              />
            </ChecklistDropdown>
          </div>
        </div>
      )}
      <div className={classes.container}>
        <FeedListComp {...props} />
      </div>
    </>
  );
};

const AssessmentToolChooseListV2 = props => {
  const {
    updateHeaderDetails,
    courseDetails,
    name,
    t,
    entityType,
    type,
    isDPRubric,
    isLearningCoursesEnabled,
  } = props;

  const [currentStep, setCurrentStep] = useState(_.first(steps)?.value);
  const [toolId, setToolId] = useState({});

  useEffect(() => {
    if (!isLearningCoursesEnabled) return;
    updateHeaderDetails({
      isTemplateSelectionStep: currentStep === "TEMPLATE_LIST",
    });
  }, [currentStep]);

  const subjectName = _.get(courseDetails, "subjects.0.name", "");

  useLayoutEffect(() => {
    if (currentStep === "TEMPLATE_LIST") {
      let label = "";
      let localeKey = "";

      // for DP Rubric set header title as subject name and for rest set title as tool name
      if (isDPRubric) {
        label = subjectName;
        localeKey = "common:dp_assessment_criteria_for_label";
      } else {
        localeKey = "common:templates_with_label";
        label = name;
      }

      const title =
        label &&
        t(localeKey, {
          label,
        });

      updateHeaderDetails({
        title,
        subtext: "",
        showBackButton: false,
      });
    }
  }, [currentStep, name, courseDetails, type]);

  const updateCurrentStep = step => setCurrentStep(step);
  const updateToolId = id => setToolId(id);

  switch (currentStep) {
    case "TEMPLATE_LIST": {
      return (
        <TemplateList
          {...props}
          updateCurrentStep={updateCurrentStep}
          updateToolId={updateToolId}
        />
      );
    }
    case "SELECT_TEMPLATE": {
      return (
        <SelectEditTemplate
          {...props}
          mode={"view"}
          type={type}
          entityType={entityType}
          toolId={toolId}
          currentStep={currentStep}
          updateCurrentStep={updateCurrentStep}
          subjectName={subjectName}
        />
      );
    }
    case "EDIT_TEMPLATE": {
      return (
        <SelectEditTemplate
          {...props}
          mode={"edit"}
          toolId={toolId}
          entityType={entityType}
          type={type}
          currentStep={currentStep}
          updateCurrentStep={updateCurrentStep}
        />
      );
    }
  }
};

const mapStateToProps = (state, ownProps) => {
  const {
    searchText,
    createdBy,
    type,
    filterConfig = [],
    assessmentFields,
    assessmentTools,
  } = ownProps;

  const organizationId = _.get(state, "login.userInfo.org_id", "");
  const isToddleLibrary = state.login.activeTab === TODDLE_LIBRARY;
  const userId = getUserId(state);
  const academicYearId = getSelectedAcademicYearId(state);

  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course",
    ""
  );

  const courseId =
    selectedCourseId ||
    _.get(state, "classRoom.courseId") ||
    _.get(state, "classRoom.selectedCourseId");

  const curriculumProgramId = isToddleLibrary
    ? _.get(state, "classRoom.curriculumProgram.id")
    : _.get(state, "platform.currentCurriculumProgram.id", null);
  const assessmentToolFilters = getAssessmentToolFilters({
    curriculumProgramId,
  });

  const { sortEntity: orderBy, direction: orderByDirection } =
    _.first(filterConfig) || {};

  const filters = {
    ...assessmentToolFilters,
    createdBy,
    searchText,
    orderBy,
    orderByDirection,
    curriculumProgramIds: [curriculumProgramId],
  };

  const isSubjectBasedGradingEnabled =
    !getIsLearningCoursesEnabled() || getIsSubjectBasedGradingEnabled();

  const isDPRubric = type === DP_ASSESSMENT_RUBRIC;
  const isStandardBasedRubric = type === STANDARD_BASED_RUBRIC;

  if (isSubjectBasedGradingEnabled && isStandardBasedRubric) {
    const subjectIds = getFieldValue({
      fields: assessmentFields,
      uid: "subjects",
    });

    filters.standardBasedRubricFilters = { subjectIds };
  }

  const gradeScaleEntityDetails = getAssessmentToolItem(
    assessmentTools,
    GRADE_SCALE_TOOL
  );

  if (!_.isEmpty(gradeScaleEntityDetails)) {
    const gradeScaleId = _.get(gradeScaleEntityDetails, "gradeScale.id");

    if (gradeScaleId) {
      filters.standardBasedRubricFilters = {
        ..._.get(filters, "standardBasedRubricFilters", []),
        gradeScaleIds: [gradeScaleId],
      };
    }
  }

  return {
    organizationId,
    filters,
    isData: true,
    isLoading: false,
    portalType: "PLANNER",
    courseId,
    isDPRubric,
    userId,
    curriculumProgramId,
    academicYearId,
  };
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, null),
  graphql(getStaffListQuery, {
    name: "getStaffList",
    skip: ({ isDPRubric }) => isDPRubric,
    options: ({ organizationId, curriculumProgramId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId,
        filters: {
          curriculumProgramIds: curriculumProgramId
            ? [curriculumProgramId]
            : [],
        },
      },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getStaffList,
      ownProps: { isData, isLoading, networkStatus: networkStatusFromProps },
    }) {
      const { node, networkStatus } = getStaffList;

      const staffList = _.get(node, "staff.edges", []);

      const staffListOptions = _.map(staffList, staff => {
        const node = _.get(staff, "node", {});
        const id = _.get(node, "id");
        const fullName = getFormattedUserName({ user: node });

        return {
          key: id,
          value: id,
          label: fullName,
        };
      });

      return {
        staffList: staffListOptions,
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        networkStatus: networkStatus || networkStatusFromProps,
      };
    },
  }),
  graphql(getOrganizationRubricsQuery, {
    name: "getOrganizationRubrics",
    skip: ({ type, showLearningCourseTools }) =>
      type !== "RUBRIC" || showLearningCourseTools,
    options: ({ organizationId, filters, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: organizationId, filters, portalType },
    }),
    props({
      getOrganizationRubrics,
      ownProps: {
        organizationId,
        filters,
        isData,
        isLoading,
        networkStatus: networkStatusFromProps,
      },
    }) {
      const organizationDetails = getOrganizationRubricsFromCache({
        id: organizationId,
        filters,
      });

      const toolList = _.get(organizationDetails, "rubrics", []);
      const networkStatus = _.get(getOrganizationRubrics, "networkStatus");
      return {
        isData: !_.isEmpty(organizationDetails) && isData,
        toolList,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        networkStatus: networkStatus || networkStatusFromProps,
      };
    },
  }),
  graphql(getOrganizationChecklistQuery, {
    name: "getOrganizationChecklist",
    skip: ({ type, showLearningCourseTools }) =>
      type !== "CHECKLIST" || showLearningCourseTools,
    options: ({ organizationId, filters, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: organizationId, filters, portalType },
    }),
    props({
      getOrganizationChecklist,
      ownProps: {
        organizationId,
        filters,
        isData,
        isLoading,
        networkStatus: networkStatusFromProps,
      },
    }) {
      const organizationDetails = getOrganizationChecklistsFromCache({
        id: organizationId,
        filters,
      });
      const toolList = _.get(organizationDetails, "checklists", []);
      const networkStatus = _.get(getOrganizationChecklist, "networkStatus");
      return {
        isData: !_.isEmpty(organizationDetails) && isData,
        toolList,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        networkStatus: networkStatus || networkStatusFromProps,
      };
    },
  }),
  graphql(getOrganizationSinglePointRubricsQuery, {
    name: "getOrganizationSinglePointRubrics",
    skip: ({ type, showLearningCourseTools }) =>
      type !== "SINGLE_POINT_RUBRIC" || showLearningCourseTools,
    options: ({ organizationId, filters, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: organizationId, filters, portalType },
    }),
    props({
      getOrganizationSinglePointRubrics,
      ownProps: {
        organizationId,
        filters,
        isData,
        isLoading,
        networkStatus: networkStatusFromProps,
      },
    }) {
      const organizationDetails = getOrganizationSinglePointRubricsFromCache({
        id: organizationId,
        filters,
      });
      const toolList = _.get(organizationDetails, "singlePointRubrics", []);
      const networkStatus = _.get(
        getOrganizationSinglePointRubrics,
        "networkStatus"
      );
      return {
        isData: !_.isEmpty(organizationDetails) && isData,
        toolList,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        networkStatus: networkStatus || networkStatusFromProps,
      };
    },
  }),
  graphql(getSubjectAndGenericTagByCourseQuery, {
    name: "getSubjectAndGenericTagByCourse",
    skip: ({ isDPRubric, courseId, showLearningCourseTools }) =>
      !isDPRubric || _.isEmpty(courseId) || showLearningCourseTools,
    options: ({ courseId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: courseId, portalType },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getSubjectAndGenericTagByCourse,
      ownProps: { isData, isLoading, networkStatus: networkStatusFromProps },
    }) {
      const courseDetails = getSubjectAndGenericTagByCourseFromCache(
        getSubjectAndGenericTagByCourse.variables
      );
      const isSubjectBasedGradingEnabled = getIsSubjectBasedGradingEnabled();
      const { subjects } = courseDetails || {};

      const genericTags =
        isSubjectBasedGradingEnabled && getIsLearningCoursesEnabled()
          ? _.flatMap(courseDetails.subjectLevelMap, "associatedLevels")
          : _.get(courseDetails, "genericTags", []);

      const subjectIds = _.map(subjects, "id");
      const genericTagsIds = _.map(genericTags, "id");

      const dpFilters = {
        subjectIds,
        genericTags: _.uniq(genericTagsIds),
      };

      const networkStatus = _.get(
        getSubjectAndGenericTagByCourse,
        "networkStatus"
      );

      return {
        isData: !_.isEmpty(courseDetails) && isData,
        dpFilters,
        courseDetails,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        networkStatus: networkStatus || networkStatusFromProps,
      };
    },
  }),
  graphql(getOrganizationDpAssessmentRubricQuery, {
    name: "getOrganizationDpAssessmentRubric",
    skip: ({ isDPRubric, showLearningCourseTools }) =>
      !isDPRubric || showLearningCourseTools,
    options: ({ organizationId, dpFilters, portalType, learningCourseId }) => {
      let updatedDpFilters = dpFilters;

      if (
        _.isEmpty(dpFilters) &&
        getIsLearningCoursesEnabled() &&
        (getIsSubjectBasedGradingEnabled() || getIsCourseBasedGradingEnabled())
      ) {
        const subjects = getAssessmentSubjects({ learningCourseId });
        const subjectLevels = getLevelsForSubjectOrCourseBasedSetup({
          learningCourseId,
        });

        const subjectIds = _.map(subjects, "id");
        const genericTagsIds = _.map(subjectLevels, "value");

        updatedDpFilters = {
          subjectIds,
          genericTags: _.uniq(genericTagsIds),
        };
      }

      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: organizationId,
          filters: updatedDpFilters,
          portalType,
        },
      };
    },
    props({
      getOrganizationDpAssessmentRubric,
      ownProps: {
        organizationId,
        isData,
        isLoading,
        networkStatus: networkStatusFromProps,
      },
    }) {
      const organizationDetails = getOrganizationDpAssessmentRubricFromCache({
        id: organizationId,
        filters: _.get(getOrganizationDpAssessmentRubric, "variables.filters"),
      });
      const toolList = _.get(organizationDetails, "dpAssessmentRubrics", []);
      const networkStatus = _.get(
        getOrganizationDpAssessmentRubric,
        "networkStatus"
      );

      return {
        isData: !_.isEmpty(organizationDetails) && isData,
        toolList: _.uniqBy(toolList, "id"),
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        networkStatus: networkStatus || networkStatusFromProps,
      };
    },
  }),
  graphql(getOrganizationRubricsAndScoreBasedRubricsQuery, {
    name: "getOrganizationRubricsAndScoreBasedRubrics",
    skip: ({ type, showLearningCourseTools }) =>
      type !== SCORE_BASED_RUBRIC || showLearningCourseTools,
    options: ({ organizationId, filters: _filters, portalType }) => {
      const { orderBy, orderByDirection, ...restFilters } = _filters;

      const filters = {
        ...restFilters,
        assessmentToolType: [RUBRIC, SCORE_BASED_RUBRIC],
      };

      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: organizationId,
          orderBy,
          orderByDirection,
          filters,
          portalType,
        },
      };
    },
    props({
      getOrganizationRubricsAndScoreBasedRubrics: { node, networkStatus },
      ownProps: { isData, isLoading, networkStatus: networkStatusFromProps },
    }) {
      const assessmentTools = _.get(node, "assessmentTools.edges", []);

      const toolList = _.map(assessmentTools, ({ node } = {}) => {
        const { assessmentToolType, assessmentToolItem } = node || {};

        return {
          ...(assessmentToolItem || {}),
          assessmentToolType,
        };
      });

      return {
        toolList,
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        networkStatus: networkStatus || networkStatusFromProps,
      };
    },
  }),
  graphql(getOrganizationStandardRubricsQuery, {
    name: "getOrganizationStandardRubrics",
    skip: ({ type, showLearningCourseTools }) =>
      type !== STANDARD_BASED_RUBRIC || showLearningCourseTools,
    options: ({ organizationId, filters, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: organizationId, filters, portalType },
    }),
    props({
      getOrganizationStandardRubrics: { node, networkStatus },
      ownProps: { isData, isLoading, networkStatus: networkStatusFromProps },
    }) {
      const toolList = _.get(node, "standardBasedRubrics", []);

      return {
        isData: !_.isEmpty(node) && isData,
        toolList,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        networkStatus: networkStatus || networkStatusFromProps,
      };
    },
  }),
  graphql(getLearningCourseAssessmentToolsQuery, {
    name: "getLearningCourseAssessmentTools",
    skip: ({ showLearningCourseTools }) => !showLearningCourseTools,
    options: ({
      learningCourseId,
      filters: { searchText, createdBy },
      type,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: learningCourseId,
        filters: {
          searchText,
          createdBy,
          assessmentToolType: type,
        },
      },
    }),
    props({
      getLearningCourseAssessmentTools: { networkStatus, node },
      ownProps: { isData, isLoading, networkStatus: networkStatusFromProps },
    }) {
      const toolListEdges = _.get(node, "assessmentTools.edges", []);
      const toolList = _.map(toolListEdges, edge => {
        // Get the node from each edge
        const edgeNode = _.get(edge, "node", {});

        // Extract the assessmentToolItem which contains the actual tool details
        const assessmentToolItem = _.get(edgeNode, "assessmentToolItem", {});

        // Return the assessmentToolItem, preserving its original structure
        return {
          ...assessmentToolItem,
        };
      });

      return {
        isData: !_.isEmpty(node) && isData,
        toolList,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        networkStatus: networkStatus || networkStatusFromProps,
      };
    },
  })
)(AssessmentToolChooseListV2);
